/**
 * Required global libraries: jQuery, Justified-Gallery, lazysizes, leaflet and showdown
 */
import { UiElement } from "../UiElement.js";
import { CDN } from "../../CDN.js";
import { VectorGraphics } from "./VectorGraphics.js";
import { ThumbnailLoopOnImageHover, ThumbnailPositionOnImageHover, createCoverOverlay } from "./ThumbnailGalleryCover.js";
import { MediaDataService, MediaDataStack, MediaBatchLoader, MediaScrollLoader } from "./MediaGalleryUtils.js"
import "./MediaGallery.css";

export { MediaGallery, MediaGalleryContent, MediaGalleryQueryParameter }

/**
 * Class to update query parameters in the media gallery
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class MediaGalleryQueryParameter {
    /**
     * Configure request parameters for the media gallery
     */
    constructor() {
        this._addQueryParameterBackwardCompatibility();

        const separateValuesKeys = ['layer'];
        const separateValuesRegex = /[,]/;
        this.queryParameterDict = vef.ui.media.QueryString.getParameter(window.location.href, separateValuesKeys, separateValuesRegex);

        this._addFallbackQueryParameters();
    }

    /**
     * Rewrite outdated query parameters
     */
    _addQueryParameterBackwardCompatibility() {
        //07.06.2023:
        const searchParams = new URLSearchParams(window.location.search);
        let rewriteURI = false;
        if (searchParams.has('event')) {
            searchParams.set('filter', `event='${searchParams.get('event')}'`);
            searchParams.delete('event');
            rewriteURI = true;
        }
        if (searchParams.has('cqlFilter')) {
            searchParams.set('filter', searchParams.get('cqlFilter'));
            searchParams.delete('cqlFilter');
            rewriteURI = true;
        }
        if (rewriteURI)
            window.history.replaceState("object or string", document.title, `?${vef.ui.media.URIComponent.decodeSafeForHistory(searchParams.toString())}`);
    }

    /**
     * Include default query parameters if they are unset
     */
    _addFallbackQueryParameters() {
        const queryParameterFallbackScenarios = [
            {
                filter: ["event like 'MSM95_52%'"],
                layer: ['photos'],
                shareID: [undefined],
                ows: ['https://maps.awi.de/services/common/media/wfs']
            },
            {
                filter: [`"event"='PS69/722-1'`],
                layer: ['videos', 'photos'],
                shareID: [undefined],
                ows: ['https://maps.awi.de/services/common/media/wfs']
            },
            {
                filter: ["(event='SO268/1_63-1' or event='SO268/2_153-1') and (dataset='935855' or dataset='957270')"],
                layer: ['videos', 'photos'],
                shareID: [undefined],
                ows: ['https://maps.awi.de/services/common/media/wfs']
            },
            {
                filter: ["event='PS81/116-3'"],
                layer: ['photos'],
                shareID: ['170928697500426'],
                ows: ['https://maps.awi.de/services/common/media/wfs']
            },
            {
                filter: [`"image-event.name"='PS137_39-1'`],
                layer: ['photos_ifdo'],
                shareID: [undefined],
                ows: ['https://maps.awi.de/services/common/media/wfs']
            },
        ]

        const queryParameterDictFallback = queryParameterFallbackScenarios[4]
        Object.keys(queryParameterDictFallback).forEach((key) => {
            if (key in this.queryParameterDict && this.queryParameterDict[key].length != 0)
                delete queryParameterDictFallback[key];
        });

        Object.assign(this.queryParameterDict, queryParameterDictFallback);
    }
}

/**
 * Class to include media data into the gallery with a data structure ready for justified gallery and lazysizes
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class MediaGalleryContent {
    /**
    * Insert a media item (photo or video) into the gallery
    * @param {string/HTMLElement} target - target HTML element, serves as anchor for the media gallery
    * @param {Object} feature - section of the wfs response containing media information for a single data item, e.g., photo_width, photo_height, photo_src, photo_srcset
    */
    static addContent(target, feature) {
        if (Array.isArray(feature)) feature = feature[0];
        const featurePropertiesHarmonized = feature["properties#harmonized"];

        const div = document.createElement("div");
        div.classList.add("pswp-gallery__item");
        div.classList.add("media-container");

        if (featurePropertiesHarmonized["mime_type"].startsWith("image")) {
            /*
            Note:
            * img width and height is required so justified gallery can calculate the grid based on the aspect ratio
            * The photo is loaded with lazyload at a later time, based on the computed widths and heights from justified gallery
            * In order to load the images via lazyload, the class 'lazyload' is included to the images after justified gallery finished the computation
            */
            const dataSrc = featurePropertiesHarmonized["photo_src"];
            div.innerHTML = `
                        <a 
                            target = "_blank"
                            href = "${dataSrc}"
                            >
                            <img
                                loading="lazy"
                                data-sizes="auto"
                                width="${featurePropertiesHarmonized["photo_width"] || MediaDataService.getFallbackDimension('width')}"
                                height="${featurePropertiesHarmonized["photo_height"] || MediaDataService.getFallbackDimension('height')}"
                                data-src = "${dataSrc}"
                                data-srcset = "${featurePropertiesHarmonized["photo_srcset"] || ''}"
                                onerror='this.removeAttribute("onerror"); this.src="${VectorGraphics.errorImage('Photo')}"; this.removeAttribute("srcset");'
                            >
                        </a>
                    `;
        }
        else if (featurePropertiesHarmonized["mime_type"].startsWith("video")) {
            const thumbnailURL = featurePropertiesHarmonized["thumbnail_src"];
            const dataSrc = featurePropertiesHarmonized["cover_src"]
            div.innerHTML = `
                        <a 
                            target = "_blank"
                            href = "${dataSrc}"
                            >
                            <img
                                loading="lazy"
                                data-sizes="auto"
                                width="${featurePropertiesHarmonized["video_width"] || MediaDataService.getFallbackDimension('width')}"
                                height="${featurePropertiesHarmonized["video_height"] || MediaDataService.getFallbackDimension('height')}"
                                data-src = "${dataSrc}"
                                data-srcset = "${featurePropertiesHarmonized["cover_srcset"] || ''}"
                                onerror='this.removeAttribute("onerror"); this.src="${VectorGraphics.errorImage('Cover')}"; this.removeAttribute("srcset");'
                                data-hover-box-thumbnail-src = "${thumbnailURL}"
                                data-text-duration="${featurePropertiesHarmonized["duration"] ? featurePropertiesHarmonized["duration"].split('.')[0] : ''}"
                                data-text-resolution="${featurePropertiesHarmonized["video_height"] ? featurePropertiesHarmonized["video_height"] : ''}"
                            >
                        </a>
                    `;
            const mediaElement = div.querySelector('img');
            if (thumbnailURL) {
                new ThumbnailPositionOnImageHover(mediaElement, "data-hover-box-thumbnail-src");
                //new ThumbnailLoopOnImageHover(mediaElement, "data-hover-box-thumbnail-src", 200);
            }
            createCoverOverlay(mediaElement.parentElement, mediaElement);
        }
        else {
            console.log(`Unsupported media type: ${featurePropertiesHarmonized["mime_type"]}`);
        }
        target.appendChild(div);
    }
}

/**
 * Class for setting up the loading of the media gallery elements
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class MediaGalleryLoader {

    /**
     * Default configuration of the media stack, batch and scroll loader
     * @param {Class} mediaGallery - dependency injection of the MediaGallery class
     */
    constructor(mediaGallery) {
        this.mediaGallery = mediaGallery;

        const mediaStackStartSize = 200;
        this.mediaStack = new MediaDataStack(this.mediaGallery.getElement(), this.mediaGallery.featureList);
        this.mediaStack.insertIntoGallery(mediaStackStartSize);

        this.batchTotalSize = 5000 - mediaStackStartSize;
        this.batchGroupSize = 100;
        this._setupBatchLoader();

        this.scrollGroupSize = 100;
        this.scrollLoadThreshold = 80;
        this.scrollElement = mediaGallery.scrollElement;
        this.galleryElement = this.mediaGallery.getElement();
        this._setupScrollLoader();

        $(this.mediaGallery.getElement()).on('jg.complete', function () {
            const mediaGallery = this.mediaGallery.getElement();
            const querySelectorImages = '.pswp-gallery__item img:not([class*="lazyload"])';
            const lazyLoadingEventName = 'lazyloading.configured';
            this.mediaGallery.prepareItemsForLazyLoading(mediaGallery, querySelectorImages, lazyLoadingEventName)
        }.bind(this))
    }

    /**
     * Set up the loading via scrolling and configure the event when the gallery is ready to insert new items
     */
    _setupScrollLoader() {
        const useCapture = true;
        const scrollLoaderStack = new MediaScrollLoader(this.mediaStack, this.scrollElement, this.galleryElement);
        window.addEventListener('scroll', function () {
            scrollLoaderStack.loadViaScrolling(this.scrollGroupSize, this.scrollLoadThreshold)
        }.bind(this), useCapture);
        this.mediaGallery.getElement().addEventListener('lazyloading.configured', function () {
            scrollLoaderStack.isLoading = false;
        }.bind(this))
    }

    /**
     * Set up loading via batches and configure the event when the gallery is ready to insert new items
     */
    _setupBatchLoader() {
        this.mediaGallery.getElement().addEventListener('lazyloading.configured', function () {
            if (typeof this.batchLoaderStack !== "object")
                this.batchLoaderStack = new MediaBatchLoader(this.mediaStack, this.batchGroupSize, this.batchTotalSize);
            if (this.mediaStack.itemsInGallery < this.batchLoaderStack.totalSizeLimit)
                this.batchLoaderStack.loadNextBatch()
        }.bind(this))
    }
}


/**
 * A media gallery with responsive and lazy image loading and a fullscreen view for single images
 * @memberof vef.ui.media
 * @extends UiElement
 * @author ckraemme <ckraemme@awi.de>
 */
class MediaGallery extends UiElement {

    /**
     * Set up a media gallery based on justified gallery, photoswipe and lazysizes
     * @param {string/HTMLElement} target - target HTML element, serves as anchor for the media gallery
     * @param {object} featureCollection - dictionary containing data information. Structure of the dictionary is based on the harmonized response of the maps.awi.de web feature service
     * @param {HTMLElement} scrollElement - element containing the vertical media gallery scrollbar
     */
    constructor(target, featureCollection, scrollElement) {
        super(target, {
            "content_init": [],
            "gallery_empty": []
        });

        this.featureCollection = featureCollection;
        this.featureList = featureCollection.features;
        this.scrollElement = scrollElement || document.documentElement;

        if (typeof this.featureList === 'undefined' || this.featureList.length == 0) {
            const eventMessage = 'no items in media gallery';
            console.log(eventMessage);
            // A timeout of zero lets the stack finish before firing the callback. Necessary to allow registration of event listeners
            setTimeout(() => this.fire('gallery_empty', eventMessage), 0);
            return;
        }
        this.setClass(["pswp-gallery", "media-gallery"]);
        this._setupLazysizes();

        this._initModules().then(() => {
            this._setupJustifiedGallery();
            this._setupLoadingOfMediaGalleryItems();
            this.fire('content_init')
        })
    }

    /**
     * Load dependencies asynchronously
     * @private
     * @returns {Promise} Promise that is resolved when loaded
     */
    _initModules() {
        return new Promise(resolve => {
            Promise
                .all([
                    CDN.import("lazysizes"),
                ])
                .then(modules => {
                    this.modules = {
                        lazysizes: modules[0],
                    }
                    resolve();
                })
        });
    }

    /**
     * Set up media gallery loading
     * @private
     */
    _setupLoadingOfMediaGalleryItems() {
        this.mediaLoader = new MediaGalleryLoader(this)
    }

    /**
     * Init the justified gallery and apply default settings
     * @private
     */
    _setupJustifiedGallery() {
        // Note: justified gallery does not know the image(s) itself, only their aspect ratios based on image width and image height
        // JG events: 'jg.resize', 'jg.complete', 'jg.rowflush', i.e., $(this.getElement()).on('jg.complete', function () {})
        $(this.getElement()).justifiedGallery({
            rowHeight: 200,
            lastRow: 'nojustify',
            margins: 3, //same as 'padding' for .gallery-overview
        });
    }

    /**
     * Set up basic configuration for lazysizes
     * @private
     */
    _setupLazysizes() {
        // must be called before importing lazysizes so the settings affect auto initialization
        // window.lazySizesConfig must be defined before importing the lazysizes module
        window.lazySizesConfig = window.lazySizesConfig || {};

        // here, the default attribute names are used
        lazySizesConfig.srcAttr = 'data-src';
        lazySizesConfig.srcsetAttr = 'data-srcset';

        // expand the calculated visual viewport area in all directions, so that elements can be loaded before they become visible. The default value is calculated depending on the viewport size of the device (default: 370-500). Recommended range: 300-1000
        lazySizesConfig.expand = window.innerHeight * window.devicePixelRatio * 3;

        // lazySizesConfig.expFactor (default: 1.5): The expFactor is used to calculate the "preload expand", by multiplying the normal expand with the expFactor which is used to preload assets while the browser is idling (no important network traffic and no scrolling). Reasonable values are between 1.5 and 4 depending on the expand option
        lazySizesConfig.expFactor = 3;

        // disable self initialization with 'false' - requires to call lazySizes.init() manually. Consequently calculate justified gallery before lazyloading images
        lazySizesConfig.init = true;

        // Note: native lazy loading can be combined with lazysizes configuration, when setting attribute loading="lazy" in addition to the class name lazyload. This allows the advantage of native features (like HTTP/2) to be used in addition to lazysizes configuration like expand factor. 
    }

    /**
     * Include the class name 'lazyload' to items selected via query selector and dispatch an event when done
     * @param {String} targetElement - target element where query selector is applied and event is dispatched
     * @param {String} querySelectorExpression - query selector expression to get the items
     * @param {String} eventName - event name that is dispatched when done
     * @private
     */
    prepareItemsForLazyLoading(targetElement, querySelectorExpression, eventName) {
        const galleryItems = targetElement.querySelectorAll(querySelectorExpression);
        //console.log('prepare items for lazyloading: ', galleryItems.length);
        galleryItems.forEach(image => {
            image.classList.add('lazyload');
        })
        targetElement.dispatchEvent(new Event(eventName));
    }
}